import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Card,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import notificationsServices from "../../services/api/service/notificationService";
import {
  notificationSelector,
  setNotification,
  setUnreadCount,
} from "../../services/redux/slices/notificationSlice";
import { dateParser } from "../../utils/formatting";
import MainHeading from "../../components/ui/MainHeading";
import { SecondaryButton } from "../../components/ui/Button";
import { decrypt, encrypt } from "../../utils/crypto";
import { authSelector } from "../../services/redux/slices/authSlice";
import { RootState } from "../../services/redux/store";
import ProfileAvatar from "../../components/common/ProfileAvatar";
import { RadioButton } from "../../components/ui/RadioButton";
import FormattedDate from "../../components/common/FormattedDate";
import Breadcrumbs from "../../components/ui/Breadcrumbs";

interface Notification {
  id: string;
  created_at: string;
  cif: string;
  cifEntity: string;
  message: string;
  status?: string;
  readStatus: boolean;
}

const NotificationListing: React.FC = () => {
  const dispatch = useDispatch();
  const { notificationData, loading } = useSelector(notificationSelector);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [selectAll, setSelectAll] = useState(false);
  const { userProfile } = useSelector(authSelector);
  const businessTypeId = useSelector(
    (state: RootState) => state?.auth?.businessType?.id
  );
  const loanCategoryData: any = useSelector(
    (state: RootState) => state?.auth?.loanCategory
  );
  const tabType = useSelector((state: RootState) => state.tabs.tabType);
  const fetchNotifications = async (pageSkip: number, pageSize: number) => {
    setIsLoading(true);
    try {
      const bodyObj: any = {};
      let cifNumber = userProfile?.cifNumber;
      bodyObj.cifNumber = cifNumber;
      bodyObj.businessType = encrypt(businessTypeId);
      bodyObj.loanCategory = tabType==="Term Loan"? encrypt(
        loanCategoryData?.TermLoan[0]?.loanCategoryId
      ): encrypt(
        loanCategoryData?.Flexi[0]?.loanCategoryId
      );
      bodyObj.pageSkip = pageSkip;
      bodyObj.pageSize = pageSize;
      const response = await notificationsServices.getNotifications(bodyObj);
      if (response?.status === 200) {
        const data = response?.data || {};
        const notificationsList: Notification[] = data?.data || [];
        const unreadCount = data?.unreadNotificationsCounts || 0;
        setTotalRows(data?.pagination?.total_rows || 0);
        dispatch(setNotification(notificationsList));
        dispatch(setUnreadCount(unreadCount));
      } else {
        console.error("Unexpected response status:", response?.status);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(page + 1, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event?.target?.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectAll(true);
    } else {
      setSelected([]);
      setSelectAll(false);
    }
  };

  useEffect(() => {
    if (selectAll) {
      setSelected(notificationData.map((n: Notification) => n.id));
    } else {
      setSelected([]);
    }
  }, [selectAll, notificationData]);

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleDeleteAll = async () => {
    try {
      const payload = { id: selected.map((id) => parseInt(id)) };
      await notificationsServices.readNotification(payload);
      dispatch(
        setNotification(
          notificationData.filter((n: Notification) => !selected.includes(n.id))
        )
      );
      setSelected([]);
    } catch (error) {
      console.error("Error deleting notifications:", error);
    }
  };

  const handleMarkAsRead = async (id: any) => {
    try {
      const payload = { id: selected.map((id) => parseInt(id)) };
      await notificationsServices.readNotification(payload);
      const updatedNotifications = notificationData.map(
        (notification: Notification) =>
          selected.includes(notification.id)
            ? { ...notification, readStatus: true }
            : notification
      );
      dispatch(setNotification(updatedNotifications));
      setSelected([]);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };
  let breadcrumbItems = [
    { label: "Dashboard", path: "/" },
    { label: "Notification" }, // Current page, no `path` needed
  ];
  return (
    <Box sx={{ width: "100%", padding: "0px" }}>
      <Box sx={{ padding: "0px 0px 18px 0px" }}>
        <Breadcrumbs items={breadcrumbItems} />
      </Box>
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{
          color: "rgba(22, 135, 148, 1)",
          fontSize: "17px",
          fontWeight: "600",
        }}
      >
        Notifications
      </Typography>
      {isLoading ? (
        <Box sx={{ padding: "50px", textAlign: "center" }}>
          <Typography variant="body1">Loading...</Typography>
        </Box>
      ) : (
        <Box sx={{ marginTop: "16px" }}>
          {notificationData?.length ? (
            <>
              {/* <Grid
                container
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Grid item>
                  <SecondaryButton
                    variant="contained"
                    onClick={handleDeleteAll}
                    disabled={selected?.length === 0}
                    sx={{ mr: 2 }}
                  >
                    Delete Selected
                  </SecondaryButton>
                  <SecondaryButton
                    variant="contained"
                    onClick={handleMarkAsRead}
                    disabled={selected?.length === 0}
                  >
                    Mark As Read
                  </SecondaryButton>
                </Grid>
              </Grid> */}
              <Card className="dashboard-container">
                <Table>
                  <TableBody>
                    {notificationData?.map(
                      (item: Notification, index: number) => (
                        <TableRow
                          key={item.id}
                          role="checkbox"
                          aria-checked={selected?.indexOf(item?.id) !== -1}
                          tabIndex={-1}
                          selected={selected?.indexOf(item?.id) !== -1}
                          onClick={(event) => handleClick(event, item?.id)}
                        >
                          {/* <TableCell
                            className="tableCellBody"
                            padding="checkbox"
                          >
                            <RadioButton
                              checked={selected?.indexOf(item?.id) !== -1}
                              onClick={() => handleMarkAsRead(item?.id)}
                            />
                          </TableCell> */}

                          <TableCell
                            className="tableCellBody"
                            sx={{
                              // whiteSpace: "nowrap",
                              // display: "grid",
                              padding: 0,
                            }}
                          >
                            <Grid container>
                              <Grid item>
                                <IconButton
                                  sx={
                                    {
                                      // paddingLeft: "40px",
                                      // gap: 1,
                                      // "&:hover": {
                                      //   backgroundColor: "transparent",
                                      //   boxShadow: "none",
                                      // },
                                    }
                                  }
                                  size="large"
                                  aria-label="account of current user"
                                  aria-controls="profile-menu"
                                  aria-haspopup="true"
                                >
                                  <ProfileAvatar
                                    name={"jhjh"}
                                    src={""}
                                    size={40}
                                  />
                                </IconButton>
                              </Grid>
                              {/* <Typography className="insidePageLable">
                                  Loan Amount
                                </Typography> */}
                            </Grid>
                          </TableCell>
                          <TableCell style={{ padding: "10px 0px" }}>
                            <Typography className="insidePageLable">
                              {item?.message}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className="tableCellBody"
                            style={{
                              width: "112px",
                              textAlign: "right",
                              padding: "10px 10px",
                            }}
                          >
                            <Typography className="insidePageLable">
                              <FormattedDate createdAt={item?.created_at} />
                              {/* {dateParser(item?.created_at, "DD-MM-YYYY")} */}
                            </Typography>

                            {/* {item?.readStatus ? (
                              <Typography variant="body2">Read</Typography>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "#721c24" }}
                              >
                                Unread
                              </Typography>
                            )} */}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Card>
               <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={totalRows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </>
          ) : (
            <Card sx={{ textAlign: "center", marginTop: "20px" }}>
              <Typography variant="body1">No Notifications</Typography>
              <Typography variant="body2">
                When you have notifications, you will see them here.
              </Typography>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default NotificationListing;
